import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import {
  ArrowLeft,
  ClipboardCheck,
  Copy,
  Signature,
  Users,
  Trophy,
  ArrowRight,
  Star,
  MessageCircle,
  Mail,
  PlayCircle,
  Clock3,
} from "lucide-react";

function Section({ id, icon: Icon, title, subtitle, children, testid }) {
  return (
    <section id={id} data-testid={testid} className="scroll-mt-24 bg-white border border-brand-linen rounded-3xl shadow-card p-6 sm:p-8">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex-shrink-0 rounded-full p-2 bg-brand-secondary/15 text-brand-secondary">
          <Icon className="w-4 h-4" strokeWidth={1.7} />
        </span>
        <div className="min-w-0">
          <h2 className="font-serif text-2xl sm:text-3xl text-brand-ink">{title}</h2>
          {subtitle && <p className="text-brand-body mt-1 text-sm sm:text-base">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-5 prose-cairn text-brand-body text-sm sm:text-base leading-relaxed space-y-4">
        {children}
      </div>
    </section>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex gap-3">
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-ink text-brand-bone text-xs flex items-center justify-center font-medium">
        {n}
      </span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

/**
 * Inline per-section screencast. Short, muted, playsInline — intended as a
 * supporting visual next to the written steps rather than the main video at
 * the top of the page.
 */
function InlineClip({ src, caption, testid }) {
  return (
    <figure className="mt-2 rounded-2xl overflow-hidden border border-brand-linen bg-brand-ink/95" data-testid={testid}>
      <video
        src={src}
        controls
        preload="metadata"
        playsInline
        muted
        className="w-full aspect-video bg-brand-ink"
      >
        Your browser can't play HTML5 video. <a className="underline" href={src}>Download the clip</a>.
      </video>
      {caption && (
        <figcaption className="px-4 py-2 text-[11px] uppercase tracking-widest text-brand-bone/70">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/**
 * Screencast at the top of the PM guide. Reads the video URL from
 * REACT_APP_PM_GUIDE_VIDEO_URL at build time:
 *   - YouTube / Vimeo embed URLs → rendered via <iframe>
 *   - .mp4 / .webm → rendered via <video controls>
 *   - Empty / unset → rendered as a tasteful placeholder so the page still
 *     looks finished before the video is published.
 */
function GuideVideo() {
  const url = (process.env.REACT_APP_PM_GUIDE_VIDEO_URL || "").trim();
  const isDirectVideo = /\.(mp4|webm|mov)(\?|$)/i.test(url);
  const isEmbed = /youtube\.com\/embed|youtu\.be|vimeo\.com|player\.vimeo\.com/i.test(url);

  return (
    <section
      data-testid="pm-guide-video"
      className="mt-8 rounded-3xl overflow-hidden border border-brand-linen bg-brand-ink text-brand-bone"
    >
      <div className="aspect-video w-full bg-brand-ink/90 flex items-center justify-center relative">
        {isDirectVideo ? (
          <video
            data-testid="pm-guide-video-player"
            controls
            preload="metadata"
            className="w-full h-full"
            src={url}
          >
            Your browser can't play HTML5 video. <a className="underline" href={url}>Download the video</a>.
          </video>
        ) : isEmbed ? (
          <iframe
            data-testid="pm-guide-video-iframe"
            src={url}
            title="PM getting started screencast"
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          // Placeholder — the video hasn't been published yet.
          <div
            data-testid="pm-guide-video-placeholder"
            className="w-full h-full flex flex-col items-center justify-center p-6 text-center bg-gradient-to-br from-brand-ink to-brand-ink/80"
          >
            <PlayCircle className="w-14 h-14 text-brand-bone/60" strokeWidth={1.2} />
            <div className="mt-4 text-sm uppercase tracking-[0.28em] text-brand-bone/60">60-second screencast</div>
            <div className="font-serif text-2xl sm:text-3xl mt-2 max-w-md">
              Watch a real course being assigned, end-to-end.
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm text-brand-bone/70">
              <Clock3 className="w-4 h-4" strokeWidth={1.7} />
              <span>Publishing soon. For now, the step-by-step below has you covered.</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default function PmGuide() {
  const contents = [
    { id: "assign", icon: ClipboardCheck, label: "Assign courses" },
    { id: "invite", icon: Copy, label: "Invite your team" },
    { id: "signoff", icon: Signature, label: "Sign off & confidence" },
    { id: "team", icon: Users, label: "Team dashboard & CSV" },
    { id: "support", icon: MessageCircle, label: "Get help" },
  ];

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <Link
          to="/admin"
          data-testid="pm-guide-back"
          className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-ink"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.7} /> Back to Admin
        </Link>

        <div className="mt-4 text-xs uppercase tracking-[0.28em] text-brand-muted">Practice Manager guide</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-ink mt-2">Getting started as a PM</h1>
        <p className="text-brand-body mt-3 max-w-2xl">
          Everything you need to run training for your practice on Calside — assigning courses, inviting staff, signing off completions and reading the team dashboard. About a 5-minute read.
        </p>

        {/* Screencast — the ~60-second video is served from REACT_APP_PM_GUIDE_VIDEO_URL
            (YouTube embed URL, Vimeo, or a direct .mp4). Falls back to a nice
            placeholder when not yet configured, so the layout is never broken. */}
        <GuideVideo />

        {/* Table of contents */}
        <nav className="mt-8 rounded-2xl border border-brand-linen bg-brand-bone/40 p-5" data-testid="pm-guide-contents">
          <div className="text-xs uppercase tracking-widest text-brand-muted mb-3">In this guide</div>
          <ul className="grid sm:grid-cols-2 gap-2">
            {contents.map((c) => (
              <li key={c.id}>
                <a
                  href={`#${c.id}`}
                  onClick={(e) => {
                    const el = document.getElementById(c.id);
                    if (el) {
                      e.preventDefault();
                      el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }}
                  className="flex items-center gap-2 text-sm text-brand-ink hover:text-brand-accent"
                >
                  <c.icon className="w-4 h-4 text-brand-muted" strokeWidth={1.7} />
                  <span>{c.label}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-brand-muted ml-auto" strokeWidth={1.7} />
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-10 space-y-8">
          <Section
            id="assign"
            testid="pm-guide-section-assign"
            icon={ClipboardCheck}
            title="Assign courses to your team"
            subtitle="Point specific staff at specific training, with an optional due date and note. They'll see it under 'Assigned to you' on their dashboard the moment you save."
          >
            <Step n={1}>
              <p>
                On <Link to="/admin" className="text-brand-accent underline">Admin</Link>, tap the primary
                <span className="mx-1 inline-flex items-center gap-1 rounded-full bg-brand-ink text-brand-bone px-2.5 py-0.5 text-xs">
                  <ClipboardCheck className="w-3 h-3" strokeWidth={1.7} /> Assign a course
                </span>
                button near the top, or scroll to the highlighted <em>Course assignments</em> panel.
              </p>
            </Step>
            <Step n={2}>
              <p>Tick one or more team members on the left. You can search by name or email if your list is long.</p>
            </Step>
            <Step n={3}>
              <p>Tick one or more courses on the right. Any combination is valid — assigning 3 courses to 5 people creates 15 assignments in one click.</p>
            </Step>
            <Step n={4}>
              <p>
                (Optional) Add a <span className="font-medium text-brand-ink">due date</span> — the panel and the learner's dashboard will show an <span className="text-brand-accent font-medium">Overdue</span> chip once it passes.
              </p>
            </Step>
            <Step n={5}>
              <p>
                (Optional) Add a short <span className="font-medium text-brand-ink">note</span> — e.g. <em>"Please complete before your appraisal on 15 May"</em> — so the learner knows why they're being asked.
              </p>
            </Step>
            <Step n={6}>
              <p>Hit <span className="font-medium text-brand-ink">Assign</span>. Done. You'll see live status (Assigned / Overdue / Completed) in the panel.</p>
            </Step>
            <InlineClip
              src="/pm-guide-screencast.webm"
              testid="pm-guide-clip-assign"
              caption="Watch · the same clip as the top of the guide · 26s"
            />
            <div className="rounded-2xl border border-brand-linen bg-brand-bone/40 p-4 text-sm">
              <div className="font-medium text-brand-ink mb-1">Good to know</div>
              <ul className="list-disc list-inside space-y-1 text-brand-body">
                <li>Assignments <em>don't</em> restrict access — everyone can still browse the whole library. This is about pointing your team at the right training and evidencing that they've done it.</li>
                <li>Re-assigning the same person the same course just updates the due date / note — no duplicates.</li>
                <li>Status flips to <span className="font-medium text-brand-ink">Completed</span> automatically when the learner passes the quiz.</li>
                <li>Remove an assignment at any time — the learner's existing progress is preserved.</li>
              </ul>
            </div>
          </Section>

          <Section
            id="invite"
            testid="pm-guide-section-invite"
            icon={Copy}
            title="Invite your team"
            subtitle="Your practice has a private invite code. Anyone who registers with that code lands directly in your team — you'll see them on the dashboard within seconds."
          >
            <Step n={1}>
              <p>
                On <Link to="/admin" className="text-brand-accent underline">Admin</Link>, scroll to <em>Invite code</em> (or use the <span className="font-medium text-brand-ink">Invite your team</span> quick-action at the top).
              </p>
            </Step>
            <InlineClip
              src="/pm-guide-invite.webm"
              testid="pm-guide-clip-invite"
              caption="Watch · smooth-scroll to invite code · 13s"
            />
            <Step n={2}>
              <p>
                Tap <span className="font-medium text-brand-ink">Copy</span> to grab the code, or use <span className="font-medium text-brand-ink">WhatsApp</span> to share a pre-written invite message including the registration link and the code.
              </p>
            </Step>
            <Step n={3}>
              <p>Your team member goes to <span className="font-mono text-brand-ink">/register</span>, chooses <em>"I have an invite code"</em>, and pastes it in.</p>
            </Step>
            <div className="rounded-2xl border border-brand-linen bg-brand-bone/40 p-4 text-sm">
              <div className="font-medium text-brand-ink mb-1">If a code leaks</div>
              <p className="text-brand-body">
                Tap <span className="font-medium text-brand-ink">Regenerate</span> in the Invite code card. The old code stops working immediately. Existing accounts are not affected — only new sign-ups need the new code.
              </p>
            </div>
          </Section>

          <Section
            id="signoff"
            testid="pm-guide-section-signoff"
            icon={Signature}
            title="Sign off completed training (and confidence)"
            subtitle="A PM sign-off is a permanent, audit-logged confirmation that you've verified the learner has completed a course. Great for appraisals, CQC / HIS inspections and CPD portfolios."
          >
            <Step n={1}>
              <p>Open the <em>Team dashboard</em> (the big learners × courses matrix on <Link to="/admin" className="text-brand-accent underline">Admin</Link>).</p>
            </Step>
            <Step n={2}>
              <p>Find a cell that shows <span className="text-brand-secondary font-medium">Passed · score X%</span>. If the learner has recorded a confidence rating (1–5 stars), you'll see it here too.</p>
            </Step>
            <Step n={3}>
              <p>
                Tap <span className="font-medium text-brand-ink">Sign off</span> in that cell. Confirm the prompt. The dashboard now shows the date and your name; the event is added to the append-only audit log.
              </p>
            </Step>
            <InlineClip
              src="/pm-guide-signoff.webm"
              testid="pm-guide-clip-signoff"
              caption="Watch · sign off a passed course · 18s"
            />
            <div className="rounded-2xl border border-brand-linen bg-brand-bone/40 p-4 text-sm">
              <div className="font-medium text-brand-ink mb-1">Sign-off vs. certificate</div>
              <ul className="list-disc list-inside space-y-1 text-brand-body">
                <li><span className="font-medium text-brand-ink">Certificate</span> — automatic on every quiz pass. Points to a public verify URL.</li>
                <li><span className="font-medium text-brand-ink">PM sign-off</span> — <em>additional</em> confirmation from you as PM that the training is done. Some Health Boards and appraisers want this.</li>
                <li><span className="font-medium text-brand-ink">Confidence rating</span> — a 1–5 star self-assessment the learner submits after the quiz. Purely for reflection; it doesn't gate anything.</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-brand-linen bg-brand-bone/40 p-4 text-sm">
              <div className="font-medium text-brand-ink mb-1">Audit log</div>
              <p className="text-brand-body">
                Scroll to <em>Audit log</em> at the bottom of <Link to="/admin" className="text-brand-accent underline">Admin</Link> for a filterable, timestamped record of every sign-off, role change, password reset and quiz pass in your practice. Append-only — nothing is ever deleted.
              </p>
            </div>
          </Section>

          <Section
            id="team"
            testid="pm-guide-section-team"
            icon={Users}
            title="Team dashboard & CSV export"
            subtitle="Every learner × every course in one grid. Search by name, tap Download CSV to hand it to an appraiser or paste it into your CPD portfolio."
          >
            <p>Each cell shows one of five states:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><span className="text-brand-muted">Not started</span> — the learner hasn't opened the course yet.</li>
              <li><span className="text-brand-ink">In progress · X%</span> — completed some lessons but hasn't yet passed the quiz.</li>
              <li><span className="text-brand-accent">Not passed · X%</span> — attempted the quiz but scored below 80%. They can retry.</li>
              <li><span className="text-brand-secondary">Passed · score X%</span> — quiz passed and certificate issued. Confidence rating (if given) shown below.</li>
              <li><span className="text-brand-secondary italic">Signed off · &lt;date&gt;</span> — you've confirmed it. Shows in the same cell.</li>
            </ul>
            <p>If you've assigned a course, the cell also shows an <span className="text-brand-secondary font-medium">Assigned</span> chip until the learner passes — <span className="text-brand-accent font-medium">Overdue</span> if the due date has slipped.</p>
            <p>
              <span className="font-medium text-brand-ink">Download CSV</span> pulls the whole matrix into one spreadsheet — perfect for CQC / HIS folders or annual training records.
            </p>
            <InlineClip
              src="/pm-guide-team.webm"
              testid="pm-guide-clip-team"
              caption="Watch · team dashboard & CSV · 15s"
            />
          </Section>

          <Section
            id="support"
            testid="pm-guide-section-support"
            icon={MessageCircle}
            title="Locked out or need a hand?"
            subtitle="You can reset your own learners' passwords straight from the Admin page. If you get locked out of your PM account, message Grant directly."
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:Grant.Anthony2@nhs.scot"
                data-testid="pm-guide-email"
                className="inline-flex items-center gap-2 rounded-full bg-brand-ink text-brand-bone px-5 py-3 hover:bg-brand-inkHover"
              >
                <Mail className="w-4 h-4" strokeWidth={1.7} /> Grant.Anthony2@nhs.scot
              </a>
              <a
                href="https://wa.me/447590335390"
                target="_blank"
                rel="noreferrer"
                data-testid="pm-guide-whatsapp"
                className="inline-flex items-center gap-2 rounded-full border border-brand-linen px-5 py-3 hover:bg-brand-bone"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.7} /> WhatsApp · 07590 335390
              </a>
            </div>
          </Section>

          <div className="pt-2">
            <Link
              to="/admin"
              data-testid="pm-guide-cta-admin"
              className="inline-flex items-center gap-2 rounded-full bg-brand-ink text-brand-bone px-6 py-3 hover:bg-brand-inkHover"
            >
              <Trophy className="w-4 h-4" strokeWidth={1.7} /> Take me to Admin
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
